import React, { useState, useRef, RefObject } from 'react';
import sanitizeHtml from "../../../utils/sanitizeHtml";
import { CardsContainerViewProps } from "./props";
import style from "./style.module.scss";
import classNames from '../../../utils/classNames';
import config from './config.json';
import TypeClassCard from '../TypeClassCard';
import TypeClassLightCard from '../TypeClassLightCard';
import button from '../../private/Button/props';
import Button from '../../private/Button';


const DisplayableButton = (props: { showButton: boolean, buttonProps: button}) => {
  if (props.showButton) {
    return (
      <div className={classNames('cardsContainerViewButton', style)}>
        <Button {...props.buttonProps} />
      </div>
    );
  }
  return null;
}

export const getDisplayCount = (isOpen: boolean, cardsLength: number, maxCards: number | undefined) => {
  if (maxCards === undefined) {
    return cardsLength;
  }
  if (isOpen) {
    return cardsLength;
  }
  if (cardsLength > maxCards) {
    return maxCards;
  }
  return cardsLength;
}

function CardsContainerView(props: CardsContainerViewProps) {
  const [ isOpen, setIsOpen ] = useState(false);
  const { title, description, cards, maxCards, maxColumns, showMoreButtonText = 'Show All', showLessButtonText = 'Show less', cardType } = props;
  const TitleHeadingElement = (title.headingElement as keyof React.JSX.IntrinsicElements) || 'h2';
  const titleRef = useRef<HTMLElement>(null);

  const handleToggleCards = () => {
    setIsOpen(!isOpen);
    if (isOpen && titleRef.current) {
      titleRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Determine how many cards to display
  const displayCount = getDisplayCount(isOpen, cards?.length || 0, maxCards);
  const shouldShowButton = maxCards !== undefined && cards !== undefined && cards.length > maxCards;

  // Determine the CSS class for column configuration
  const getCardsContainerClass = () => {
    return `${classNames(`cardsContainerViewCardsMax${maxColumns}`, style)} type-cards-container`;
  };

  const buttonProps = {
    ...config.buttonProps,
    onClick: handleToggleCards,
    isOpen: isOpen,
    Label: {
      text: isOpen ? showLessButtonText : '+ ' + showMoreButtonText + ' (' + (cards?.length || 0) + ')'
    },
    e2e: 'showMoreButton'
  }

  const HeadingComponent = TitleHeadingElement as React.ElementType;

  return (
    <div className={classNames('cardsContainerView', style)}>
      <div className={'type-description'}>
        <div className={'type-text-container'}>
          <HeadingComponent
            {... (typeof title.headingElement === 'string' && titleRef) && { ref: titleRef as RefObject<HTMLHeadingElement> }}
            className={classNames('cardsContainerViewTitle', style)}
          >
            {title.text}
          </HeadingComponent>
          <p className={classNames('cardsContainerViewDescription', style)}
             dangerouslySetInnerHTML={{__html: sanitizeHtml(description)}}/>
        </div>
        <div className={getCardsContainerClass()}>
          {cards && cards.map((card, index) => (
            <div
              key={index}
              className={classNames('card', style)}
              style={{
                display: index < displayCount ? 'block' : 'none'
              }}
            >
              {cardType === "light" ? <TypeClassLightCard {...card} /> : <TypeClassCard {...card} />}
            </div>
          ))}
        </div>
      </div>
      <DisplayableButton showButton={shouldShowButton} buttonProps={buttonProps} />
    </div>
  );
}

export default React.memo(CardsContainerView);
